export const en = {
  scene: {
    loading: 'Loading the background…',
    recovering: 'Restoring the image…',
    unavailable: 'Could not load the background',
    retryHint: 'Trying again. If loading does not finish, reopen the app.',
  },
  bootstrap: {
    unsupportedBrowser: {
      title: 'This browser is not supported',
      description: 'App graphics are unavailable in your current browser configuration. Open Neiro Oracle in another browser from the list below.',
      browsers: 'Supported browsers',
      requirement: 'Use an up-to-date version with WebGL graphics available. Browser or device settings may restrict graphics support.',
      safari: 'Safari · iPhone, iPad and Mac',
    },
    loadingSpace: 'Preparing your space…',
    loadingOracle: 'Opening Oracle…',
    startupError: 'Could not start the app',
    retry: 'Try again',
    staleTelegram:
      'Your Telegram session has expired. Close and reopen the Mini App.',
    authTelegramError: 'Could not confirm Telegram sign-in',
    updateTelegramError:
      'Could not update the Mini App. Close it completely and reopen it.',
    missingTelegramData:
      'Telegram did not provide launch data. Close and reopen the Mini App.',
  },
  // Common
  back: 'Back',
  close: 'Close',
  loading: 'Loading…',
  routeReading: {
    loading: 'Opening the reading…',
    error: 'Reading unavailable',
    errorHint: 'It may have been deleted or belong to another profile.',
    retry: 'Try again',
  },
  save: 'Save',
  tomorrow: 'Tomorrow',
  collapse: 'Collapse',

  // App Title
  app: {
    title: 'NEIRO ORACLE',
    subtitle: 'Divination System v{{version}}',
    version: 'v{{version}}',
  },

  oracleGuide: {
    eyebrow: 'Neiro Oracle',
    title: 'How Oracle works',
    introduction: 'Two ways to consult the I Ching for different needs.',
    accessibilityLabel: 'Guide to Daily Sign and readings',
    open: 'How to use Oracle',
    close: 'Close guide',
    understood: 'Got it',
    daily: {
      title: 'Daily Sign',
      body: 'No question is needed. It gives you a theme and practical guide for the day.',
    },
    reading: {
      title: 'Reading',
      body: 'State one specific question and hold it in mind while you cast the coins. The reading shows the present situation, its changes, and a possible direction.',
    },
    journal: {
      title: 'Journal',
      body: 'Daily Signs and readings share one history. You can record a decision after a reading.',
    },
    example: {
      title: 'A useful question',
      body: '“What should I consider before speaking with my team?”',
    },
  },

  // Daily Details
  dailyDetails: {
    title: 'Details',
    loading: 'Loading the sign…',
    loadError: 'The sign could not be loaded in the selected language.',
    retry: 'Try again',
    fullText: 'Theme for today',
    situationEssence: 'Question to check',
    innerTension: 'Keep in mind',
    action: 'What to do today',
    askAbout: 'Ask About This',
    introduction:
      'This Daily Sign needs no question. It gives you a theme and practical guide for the day.',
  },

  classicalText: {
    title: 'CLASSICAL TEXT',
    future: 'CLASSICAL · FUTURE SIGN',
    judgment: 'HEXAGRAM STATEMENT',
    lines: 'LINE STATEMENTS',
    parallelLines: 'HEXAGRAM LINES',
    line: 'Line {{number}}',
    lineStatement: 'CLASSICAL STATEMENT',
    sourceLabel: 'I Ching',
    rulingLine: 'RULING LINE',
    specialLine: 'Special statement',
  },

  // Home Screen
  home: {
    title: 'NEIRO ORACLE',
    subtitle: 'Divination System v{{version}}',
    statusText: '> All systems operational',
    daily: {
      title: 'Daily Sign',
      caption: 'No question · a theme and guide for your day',
      actionTitle: 'Action for Today',
      askNow: 'Start a reading',
      moreDetails: 'Read More',
      hideDetails: 'Hide Details',
      detailsTitle: 'Full Description',
      lastResult: 'Last result',
      noLastResult: 'none',
      emptyState: 'No readings yet. Start a reading →',
      empty: {
        subtitle: 'Tap to receive today’s sign',
        hint: 'One-tap ritual',
        button: 'Get the Sign',
      },
      loading: {
        searching: 'Searching hexagram…',
        revealing: 'Revealing six lines…',
      },
      pinnedToday: 'Pinned for today',
      newSign: 'New sign',
      resetReasons: {
        title: 'Reason for update',
        sleep: 'Woke up after sleep',
        situationChanged: 'Situation has changed',
        other: 'Other',
      },
      resetConfirm: {
        title: 'Update sign?',
        message:
          'Update sign for a new waking cycle? Current will remain in journal.',
        confirm: 'Update',
        cancel: 'Cancel',
      },
      nextUpdate: {
        prefix: 'Next new sign —',
        afterSleep: 'after next sleep',
        tomorrow: 'tomorrow at 07:00',
        or: 'or',
        available: 'available now',
      },
      updated: 'Sign updated — new waking cycle',
      limitReached: 'Update limit reached for today',
      disclaimer:
        'For self-reflection and entertainment. Not medical or financial advice.',
    },
    question: {
      title: 'Have a specific question?',
      subtitle:
        'A reading explores the situation, its changes, and a possible direction',
      askNow: 'Start a reading',
      hint: 'No theme. Will show future hexagram, current and changing lines',
    },
    buttons: {
      iching: 'I-CHING ORACLE',
      designSystem: 'DESIGN SYSTEM',
      simpleTest: 'SIMPLE TEST',
      hexagramTest: 'HEXAGRAM TEST',
    },
  },

  // I-Ching Screen
  iching: {
    title: 'I-CHING ORACLE',
    subtitle: 'MOBILE v{{version}}',
    throwCoins: 'Throw coins',
    castingHexagram: 'Casting hexagram…',
    generationError:
      'The sign did not form. Check your connection and try the ritual again.',
    retry: 'Try again',
    castingWisdom: {
      1: 'A journey of a thousand li begins with a single step',
      2: 'Do not fear going slowly; fear standing still',
      3: 'The soft overcomes the hard, the still overcomes the restless',
      4: 'Change is the only constant under heaven',
      5: 'The wise adapt like water to the shape of the vessel',
      6: 'When the sign is formed, the answer is already within you',
    },
    instruction: 'Tap to cast your hexagram and receive guidance',
    linkActive: 'LINK ACTIVE',
    backButton: '← HOME',
    newDivination: 'NEW DIVINATION',

    sections: {
      hexagrams: 'HEXAGRAMS',
      interpretation: 'INTERPRETATION',
      aiInsights: 'AI INSIGHTS',
      general: 'GENERAL INTERPRETATION',
      love: 'LOVE & RELATIONSHIPS',
      work: 'WORK & CAREER',
      health: 'HEALTH & WELLBEING',
      travel: 'TRAVEL & JOURNEY',
      changingLines: 'CHANGING LINES',
      futureHexagram: 'POSSIBLE STATE',
      structuralFocus: 'WHAT HOLDS THE SITUATION',
    },

    hexagram: {
      current: 'CURRENT',
      future: 'POSSIBLE',
      present: 'PRESENT SITUATION',
      outcome: 'POSSIBLE DIRECTION',
      changing: 'CHANGING LINES',
      structural: 'WHAT HOLDS IT TOGETHER',
    },

    insights: {
      analyzing: 'Building the reading...',
      transformation:
        '“{{current}}” may shift toward “{{future}}”. Changing lines: {{changes}}.',
      stable:
        'No lines are changing. The situation keeps its current direction.',
    },

    changingLines: 'Changing Lines',
    changingLinesDescription:
      'These lines show where the situation may change.',
    governingLineHint: 'The crown marks a governing line of this hexagram.',
    changingLinesTitle: 'Changing Lines ({{count}})',
    structuralFocusTitle: 'What holds the situation together',
    structuralFocusDescription:
      'When no lines are changing, these positions show what currently holds the situation together.',
    lineNumber: 'Line {{number}}',
    changingLinesNote:
      'Read these lines closely: they show where the change is happening.',
  },

  // Ask Flow
  ask: {
    throw: {
      prepare: 'State your question briefly and hold it in mind.',
      tips: {
        heart: 'Say what matters to you',
        specific: 'Describe the specific situation',
        openEnded: 'Ask what you should consider',
      },
      example: 'For example: “What should I consider before deciding?”',
      guide: {
        title: 'Preparation',
        bestTime: 'Ask when you are ready to consider the answer calmly',
        todayGood: 'Leave time to check the facts before an important decision',
      },
    },
    result: {
      sections: {
        future: 'Possible direction',
        present: 'What is happening now',
        changes: 'Reading focus',
        structuralFocus: 'What holds it together',
        advice: 'How to act',
      },
      accessibility: {
        advice: 'How to act. Advice',
      },
      content: {
        check: 'Question to check',
        caution: 'Keep in mind',
        primaryLine: 'Primary focus',
        secondaryLine: 'Additional detail',
        relatingHint:
          'The relating hexagram offers another theme to compare with the situation.',
        visibleStructure: 'How the situation is taking shape',
        resultStructure: 'How the situation may develop',
        hiddenDynamic: 'What is happening underneath',
        hiddenCondition: 'What may affect the outcome',
        corePattern: 'What to notice',
        coreTension: 'Where the difficulty lies',
        question: 'Your question',
        questionContext: 'In the context of the question',
        currentAction: 'What to do now',
        entryCondition: 'How to move toward it',
        progressMarker: 'How to recognize progress',
      },
      changeDepth: {
        stable:
          'No lines are changing. The section below shows what currently holds the situation together.',
        boundary: 'The change affects how the situation begins or ends.',
        internal: 'The change affects what is happening inside the situation.',
        systemic:
          'The change reaches the foundation of the situation and may alter its course.',
      },
      buttons: {
        decide: 'Make decision',
      },
      more: 'Read more',
      skeleton: {
        loading: 'Loading…',
      },
    },
    acceptDecision: {
      back: 'Back',
      title: 'Make decision',
      subtitle: 'Add context so you can remember what this was about later.',
      reading: 'Decision basis',
      questionLabel: 'What is this decision about?',
      questionPlaceholder: 'Briefly describe the situation or question…',
      questionHint: 'Optional. This context will be saved in the journal.',
      confirm: 'Accept and save',
      saving: 'Saving…',
      cancel: 'Cancel',
      errorTitle: 'Decision was not saved',
      errorMessage: 'Please try again.',
    },
  },

  // Themes
  themes: {
    title: 'THEME',
    dark: 'DARK',
    light: 'LIGHT',
    autumn: 'AUTUMN',
    active: 'ACTIVE',
    dark_description: 'Dark surfaces with muted accents',
    light_description: 'Clean, modern light theme',
    autumn_description: 'Warm, earthy tones inspired by fall',
  },

  // Common UI elements
  ui: {
    screenTitle: 'SCREEN TITLE',
    info: 'INFO',
    language: 'LANGUAGE',
    interfaceSettings: 'Language and theme',

    // Button loading states
    loadingStates: {
      loading: 'Loading…',
      processing: 'PROCESSING...',
      connecting: 'CONNECTING...',
      deleting: 'DELETING...',
    },
  },
  // Tabs
  tabs: {
    home: 'Home',
    journal: 'Journal',
    hexagrams: 'Hexagrams',
    profile: 'Profile',
  },
  desktop: {
    oracle: 'Oracle',
    brandSubtitle: 'I Ching for decisions',
    appearance: 'Appearance',
    theme: 'Interface theme',
  },
  hexagrams: {
    title: 'Hexagrams',
    subtitle: 'All 64 I Ching signs — choose the order that suits you',
    hint: 'Choose a sign to open its interpretation',
    loading: 'Loading the hexagram catalog…',
    error: 'The hexagram catalog could not be loaded.',
    retry: 'Try again',
    order: {
      title: 'Order',
      kingWen: '1–64',
      'king-wen': '1–64',
      fuXi: 'Fu Xi',
      'fu-xi': 'Fu Xi',
      mawangdui: 'Mawangdui',
    },
    matrix: {
      accessibilityLabel: 'Matrix of 64 hexagrams. Order: {{order}}',
      cellAccessibilityLabel: 'Hexagram {{number}}. {{name}}',
    },
    modal: {
      accessibilityLabel: 'Hexagram {{number}}. {{name}}',
      number: 'Hexagram {{number}}',
      close: 'Close hexagram details',
      modernTitle: 'Modern interpretation',
      pattern: 'What is happening',
      tension: 'Where the difficulty lies',
      structureTitle: 'Structure of the situation',
      visibleStructure: 'How the situation is taking shape',
      hiddenDynamic: 'What is happening underneath',
      rolesTitle: 'Role of the hexagram',
      initialRole: 'When the sign describes the present situation',
      resultingRole: 'When the sign becomes a possible state',
      roleSummary: 'Meaning of the role',
      action: 'How to act',
      entry: 'How to enter this state',
      marker: 'Sign of progress',
      linesTitle: 'Six lines',
      spheresTitle: 'Action for your topic',
      spheres: {
        general: 'Situation or choice',
        newjob: 'New job',
        work: 'Current work',
        love: 'Relationships',
        project: 'Project',
      },
      dailyTitle: 'Daily guide',
      dailyFocus: 'Theme for the day',
      dailyAction: 'Practical step',
    },
  },
  // Journal
  journal: {
    actionsFor: 'Actions for “{{title}}”',
    entryActions: 'Entry actions',
    deleteConfirm: 'Delete entry?',
    deleteEntry: 'Delete',
    cancelDelete: 'Cancel',
    deleting: 'Deleting…',
    deleteDailyHint:
      'This sign will be removed. If it is current, the latest remaining sign for that date will take its place. The first daily sign will be kept.',
    deleteReadingHint:
      'This reading and its accepted decision will be permanently deleted.',
    deleteError: 'Could not confirm deletion. Please try again.',
    selectEntry: 'Select a journal entry',
    entryNotFound: 'Entry not found. Select another entry in the list.',
    title: 'Journal',
    filters: { all: 'All', decisions: 'Readings', daily: 'Daily Sign' },
    searchPlaceholder: 'Search by title and note',
    empty: 'Your readings will appear here',
    emptyHint: 'Your first reading appears after “Start a reading”',
    emptyAll: 'Your journal entries will appear here',
    emptyAllHint: 'Readings and daily signs stay together in one timeline',
    emptyDecisions: 'Your readings will appear here',
    emptyDecisionsHint: 'Your first reading appears after “Start a reading”',
    emptyDaily: 'Your daily signs will appear here',
    emptyDailyHint: 'Get your first sign from the Oracle screen',
    error: 'Journal could not be loaded',
    errorHint: 'Check your connection and try again',
    retry: 'Try again',
    labels: {
      decision: 'Reading',
      accepted: 'Reading · decision recorded',
      daily: 'Daily Sign',
      question: 'Question',
    },
  },
  // Profile
  profile: {
    title: 'Profile',
    account: {
      title: 'Account',
      loginTelegram: 'Continue with Telegram',
      loginHint:
        'Account history will open across devices; local entries will stay on this device',
      loginUnavailable: 'Sign-in will appear after Telegram Login is connected',
      synchronized: 'Account data is synchronized across devices',
      logout: 'Sign out',
      loginErrorTitle: 'Sign-in failed',
      loginError: 'Telegram did not confirm the sign-in. Please try again.',
      logoutErrorTitle: 'Sign-out failed',
      logoutError: 'Check your connection and try again.',
    },
    subscription: {
      title: 'Subscription',
      active: 'Active',
      expired: 'Expired',
      free: 'Free mode',
      activeUntil: 'Active until {{date}}',
      manage: 'Manage subscription',
      connect: 'Get Neiro+',
      restore: 'Restore purchases',
      hint: 'Access to extended interpretations, comparisons and personal advice.',
      restored: 'Purchases restored',
      restoreError: 'Failed to restore purchases',
      purchaseError: 'Could not start the App Store subscription',
      loading: 'Checking status...',
      benefits: {
        extendedInterpretations: 'Extended interpretations',
        personalAdvice: 'Personal advice',
        comparisons: 'Compare options',
      },
      whatsIncluded: "What's included?",
    },
    notifications: {
      title: 'Notifications',
      morning: 'Morning',
      morningHint: 'Choose a time and where to receive your morning sign',
      morningEnabledDestinationsHint:
        'The daily sign resets at {{time}} · {{destinations}}',
      morningDisabledDestinationsHint:
        'The daily sign resets at {{time}}; notifications are off',
      destinations: {
        title: 'Send reminders to',
        local: 'This Mac',
        telegram: 'Telegram',
      },
      timeHint: 'Morning time',
      invalidTime: 'Enter time as HH:MM, for example 09:00',
      timePicker: {
        title: 'When does your morning begin?',
        descriptionEnabled:
          'At this time, your daily sign resets and the Telegram bot sends a reminder.',
        descriptionEnabledLocal:
          'At this time, your daily sign resets and macOS shows a reminder.',
        descriptionEnabledMultiple:
          'At this time, your daily sign resets and selected channels send a reminder.',
        descriptionDisabled:
          'At this time, your daily sign resets. Notifications will remain off.',
        descriptionChannelUnavailable:
          'At this time, your daily sign resets. Reminders will begin when the Telegram channel becomes available.',
        descriptionChannelUnavailableLocal:
          'At this time, your daily sign resets. macOS will ask for notification permission.',
        quickChoice: 'Quick choice',
        setPreset: 'Set {{time}}',
        hours: 'Hours',
        minutes: 'Minutes',
        decreaseHours: 'Decrease hours by one',
        increaseHours: 'Increase hours by one',
        decreaseMinutes: 'Decrease minutes by five',
        increaseMinutes: 'Increase minutes by five',
        directEntryHint: 'You can enter an exact value with the keyboard',
        invalidHours: 'Enter an hour from 00 to 23',
        invalidMinutes: 'Enter minutes from 00 to 59',
      },
      loadError: 'Could not load the morning setting',
      saveError: 'Could not save the morning setting',
      retry: 'Retry',
      testSent: 'Test message sent to Telegram',
      testSentLocal: 'A test notification will appear in two seconds',
      testSentMultiple: 'Test reminders were sent to the selected channels',
      testError: 'Could not send the test message',
      channel: {
        active: 'Telegram bot is ready',
        unverified: 'Open the Telegram bot and confirm message delivery',
        blocked: 'The Telegram bot is blocked. Unblock it in Telegram',
        disconnected: 'The Telegram bot connection was lost',
        not_connected: 'Telegram bot is not connected yet',
      },
      localChannel: {
        active: 'macOS notifications are allowed',
        unverified: 'macOS notification permission is not confirmed',
        blocked: 'Allow notifications in macOS Settings',
        disconnected: 'macOS notifications are currently unavailable',
        not_connected: 'Permission will be requested when you enable reminders',
      },
      testNotification: 'Send test',
    },
    preferences: {
      title: 'Preferences',
      haptics: 'Haptic feedback',
      hapticsHint: 'Light vibration on throw and save',
    },
    classicalText: {
      title: 'Hexagram texts',
      hint: 'Choose the modern reading, the received Zhouyi text, or both layers',
      modern: 'Modern',
      classical: 'Classical',
      parallel: 'Both',
    },
    language: {
      interface: 'Interface language',
      russian: 'Русский',
      english: 'English',
    },
    data: {
      title: 'Data and privacy',
      exportJournal: 'Export journal',
      exportPeriod: 'Period',
      allData: 'All data',
      last30Days: 'Last 30 days',
      export: 'Export',
      exportError: 'Failed to export',
      deleteAccount: 'Delete personal data and account',
      deleteAccountHint:
        'Personal data, history, preferences and the account will be deleted from the app',
      deleteAccountConfirm:
        'Profile data, journal, preferences and the account will be deleted. The next sign-in creates a new account. Only content-free technical receipts remain: Telegram for a 7-day replay-protection window and deletion confirmation for a 30-day window. Receipts are automatically cleaned up after those windows.',
      deleteAccountError:
        'Could not delete personal data and the account. Check the connection and try again.',
      deleteLocalData: 'Delete all personal data',
      deleteLocalDataHint:
        'History and preferences stored by this app on the device will be deleted',
      deleteLocalDataConfirm:
        'All data stored by this app on the device will be deleted. A clean initial screen will open afterwards.',
      deleteLocalDataError: 'Could not delete this app data. Please try again.',
      cancel: 'Cancel',
      deleteAccountAction: 'Delete account',
      deleteLocalDataAction: 'Delete data',
      privacy: 'Privacy policy',
      terms: 'Terms of use',
      disclaimer:
        'For self-reflection and entertainment. Not medical or financial advice.',
    },
    support: {
      title: 'Support',
      guide: 'How Oracle works',
      guideHint: 'Daily Sign, readings, and the Journal',
      contact: 'Contact support',
      rate: 'Rate app',
      rateNow: 'Rate now',
      version: 'Version {{version}}',
      devTools: 'Dev Tools',
      devToolsDesc: 'Remote-config, push tests, mock data (placeholder)',
    },
    about: {
      title: 'About',
      version: 'Version {{version}}',
      platform: '{{platform}}',
    },
    resolveNow: 'Resolve now',
  },
};
